// Confirming a password account's email address.
//
// Not asked at sign-up: someone should see their bot work before being sent to
// their inbox. It is asked in two places instead:
//   - a banner across the dashboard, which can be put off for the session, so
//     password resets and handoff alerts reach the right address;
//   - before checkout, where it cannot be put off: a paying customer on an
//     address nobody confirmed could never get a password reset or a receipt
//     (the server refuses checkout too, with code "verify_email").
// Google sign-ins arrive confirmed and never see either.
//
// Someone who mistyped their address at sign-up can give the right one instead
// ("Wrong address?"): Firebase sends the link there, and following it changes
// the account's address to it (verifyBeforeUpdateEmail). The server copies a
// confirmed address onto its own records (functions/index.js syncStoredEmail).
// Firebase may end the session when the address changes; then the page asks
// them to sign in again, with the new address.
//
// The email is ours (sent by the server, styled like every other, linking to
// buildbotsnow.com/account/confirm); Firebase only makes the code. Clicking it
// confirms the address in another tab;
// this page notices by checking again when it regains focus and every few
// seconds while it waits, then refreshes the session so the server sees it,
// and tells the server, which connects the address to any bot of theirs that
// sends handoffs nowhere.

import { useCallback, useEffect, useState } from "react";
import type { User } from "firebase/auth";
import { useAuth } from "../context/AuthContext";
import * as api from "../lib/api";
import { Button, Notice, TextInput } from "./ui";

const LATER_KEY = "buildbotsnow.confirmEmailLater";
const CHECK_EVERY_MS = 5000;

export function needsEmailConfirmation(user: User | null | undefined): boolean {
  return Boolean(user && !user.emailVerified && (user.providerData ?? []).some((p) => p.providerId === "password"));
}

export function useEmailConfirmation() {
  const { user } = useAuth();
  const [confirmed, setConfirmed] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(0);
  // A corrected address the link was sent to, until it is confirmed.
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  // The address changed and Firebase ended the session: sign in again.
  const [signInAgain, setSignInAgain] = useState(false);
  const needed = needsEmailConfirmation(user) && !confirmed;

  const check = useCallback(async () => {
    if (!user || !needsEmailConfirmation(user)) return !needsEmailConfirmation(user);
    try {
      await user.reload();
      if (!user.emailVerified) return false;
      if (pendingEmail && user.email !== pendingEmail) return false;
      // A fresh token carries the confirmation to the server.
      await user.getIdToken(true);
      const result = await api.emailConfirmed().catch(() => null);
      setConnected(result?.connected ?? 0);
      setConfirmed(true);
      return true;
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (pendingEmail && (code === "auth/user-token-expired" || code === "auth/user-not-found" || code === "auth/invalid-user-token")) {
        setSignInAgain(true);
      }
      return false;
    }
  }, [user, pendingEmail]);

  async function send() {
    if (!user) return;
    setSending(true);
    setError(null);
    try {
      await api.sendConfirmationEmail(pendingEmail ?? undefined);
      setSent(true);
    } catch (err) {
      setError(linkError(err));
    } finally {
      setSending(false);
    }
  }

  // The right address instead of the one typed at sign-up.
  async function changeTo(address: string) {
    if (!user) return false;
    const next = address.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
      setError("That does not look like an email address.");
      return false;
    }
    if (next === (user.email || "").toLowerCase()) {
      setError("That is the address you have now.");
      return false;
    }
    setSending(true);
    setError(null);
    try {
      await api.sendConfirmationEmail(next);
      setPendingEmail(next);
      setSent(true);
      return true;
    } catch (err) {
      setError(linkError(err));
      return false;
    } finally {
      setSending(false);
    }
  }

  // While a link is out: check when the page comes back into view, and every
  // few seconds.
  useEffect(() => {
    if (!sent || !needed) return;
    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);
    const timer = setInterval(() => void check(), CHECK_EVERY_MS);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(timer);
    };
  }, [sent, needed, check]);

  return { email: pendingEmail ?? user?.email ?? "", needed, confirmed, sent, sending, error, connected, signInAgain, send, changeTo, check };
}

// The server says what went wrong in words meant for the person (a taken
// address, too many links); anything else is a plain retry.
function linkError(err: unknown): string {
  if (err instanceof api.ApiError && err.status < 500) return err.message;
  return "The link could not be sent. Try again.";
}

// "Wrong address?": a field for the right one, folded away until asked for.
function ChangeAddress({ onChange, busy }: { onChange: (address: string) => Promise<boolean>; busy: boolean }) {
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState("");
  if (!open) {
    return (
      <button type="button" className="text-sm font-medium underline" onClick={() => setOpen(true)}>
        Wrong address? Change it
      </button>
    );
  }
  return (
    <form
      className="flex w-full flex-wrap items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        void onChange(address).then((ok) => ok && setOpen(false));
      }}
    >
      <label className="min-w-0 flex-1 text-sm">
        <span className="mb-1 block">Your correct email address</span>
        <TextInput type="email" autoComplete="email" value={address} onChange={(event) => setAddress(event.target.value)} />
      </label>
      <Button type="submit" variant="secondary" busy={busy}>
        Send the link there
      </Button>
    </form>
  );
}

// The address changed and the session ended with it.
function SignInAgain({ email }: { email: string }) {
  const { signOut } = useAuth();
  return (
    <Notice tone="success">
      Your email is now <strong>{email}</strong>. Sign in again with it to carry on.{" "}
      <button type="button" className="font-medium underline" onClick={() => void signOut()}>
        Sign in again
      </button>
    </Notice>
  );
}

// Across the dashboard, until confirmed or put off for this session.
export function EmailConfirmBanner() {
  const confirmation = useEmailConfirmation();
  const [later, setLater] = useState(() => {
    try {
      return sessionStorage.getItem(LATER_KEY) === "1";
    } catch {
      return false;
    }
  });

  if (confirmation.confirmed) {
    return (
      <div className="mb-6">
        <Notice tone="success">
          Email confirmed.{" "}
          {confirmation.connected > 0
            ? `Handoffs from ${confirmation.connected === 1 ? "your bot" : `${confirmation.connected} of your bots`} now go to ${confirmation.email}.`
            : ""}
        </Notice>
      </div>
    );
  }
  if (confirmation.signInAgain) {
    return (
      <div className="mb-6">
        <SignInAgain email={confirmation.email} />
      </div>
    );
  }
  if (!confirmation.needed || later) return null;

  return (
    <div className="mb-6" data-testid="confirm-email-banner">
      <Notice tone="warning">
        <p>
          {confirmation.sent ? (
            <>
              We sent a link to <strong>{confirmation.email}</strong>. Click it, and this page will notice by itself.
            </>
          ) : (
            <>
              Confirm your email address, so password resets and handoff alerts reach <strong>{confirmation.email}</strong>.
            </>
          )}
        </p>
        {confirmation.error && <p className="mt-2 text-red-700">{confirmation.error}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button variant={confirmation.sent ? "secondary" : "primary"} busy={confirmation.sending} onClick={() => void confirmation.send()}>
            {confirmation.sent ? "Send it again" : "Send confirmation link"}
          </Button>
          <button
            type="button"
            className="text-sm font-medium underline"
            onClick={() => {
              setLater(true);
              try {
                sessionStorage.setItem(LATER_KEY, "1");
              } catch {
                // Hidden for this page view only.
              }
            }}
          >
            Later
          </button>
          <ChangeAddress onChange={confirmation.changeTo} busy={confirmation.sending} />
        </div>
      </Notice>
    </div>
  );
}

// In front of checkout: the same link, and no "later". Calls onConfirmed once
// the address is confirmed, so checkout carries on by itself.
export function ConfirmEmailGate({ onConfirmed, onCancel }: { onConfirmed: () => void; onCancel: () => void }) {
  const confirmation = useEmailConfirmation();

  useEffect(() => {
    if (confirmation.confirmed) onConfirmed();
  }, [confirmation.confirmed, onConfirmed]);

  if (confirmation.signInAgain) return <SignInAgain email={confirmation.email} />;

  return (
    <div data-testid="confirm-email-gate">
      <Notice tone="info">
        <p className="font-medium">Confirm your email to subscribe</p>
        <p className="mt-1">
          {confirmation.sent ? (
            <>
              We sent a link to <strong>{confirmation.email}</strong>. Click it, and checkout opens here by itself.
            </>
          ) : (
            <>
              Your receipts and password resets go to <strong>{confirmation.email}</strong>, so we check it is yours before you
              pay.
            </>
          )}
        </p>
        {confirmation.error && <p className="mt-2 text-red-700">{confirmation.error}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button variant={confirmation.sent ? "secondary" : "primary"} busy={confirmation.sending} onClick={() => void confirmation.send()}>
            {confirmation.sent ? "Send it again" : "Send confirmation link"}
          </Button>
          <button type="button" className="text-sm font-medium underline" onClick={onCancel}>
            Cancel
          </button>
          <ChangeAddress onChange={confirmation.changeTo} busy={confirmation.sending} />
        </div>
      </Notice>
    </div>
  );
}
